import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Settings2, TrendingDown } from 'lucide-react';
import { AdjustmentSettings } from '@/types/adjustment-filters';
import { calculateAdjustedPrices, calculateAdjustmentSummary } from '@/utils/adjustmentCalculations';
import { formatCurrency } from '@/lib/utils';

interface AdjustmentSummaryCardProps {
  lineItems: any[];
  adjustmentSettings: AdjustmentSettings;
}

export const AdjustmentSummaryCard: React.FC<AdjustmentSummaryCardProps> = ({
  lineItems,
  adjustmentSettings
}) => {
  const [summary, setSummary] = useState<any>(null);

  useEffect(() => {
    const calculate = async () => {
      const adjustedPrices = await calculateAdjustedPrices(lineItems, adjustmentSettings);
      const calculatedSummary = calculateAdjustmentSummary(adjustedPrices);
      setSummary(calculatedSummary);
    };

    if (lineItems.length > 0 && (adjustmentSettings.filters.length > 0 || adjustmentSettings.discountPercentage)) {
      calculate();
    }
  }, [lineItems, adjustmentSettings]);

  if (!summary || summary.totalSavings === 0) {
    return null;
  }

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Settings2 className="h-4 w-4" />
          Active Adjustments
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Original Total</span>
          <span className="font-mono">{formatCurrency(summary.originalTotal)}</span>
        </div>

        {summary.totalMarkupRemoved > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Markup Removed ({summary.itemsWithMarkupRemoved} items)
            </span>
            <span className="font-mono text-primary">
              -{formatCurrency(summary.totalMarkupRemoved)}
            </span>
          </div>
        )}

        {summary.totalDiscountApplied > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Discount Applied</span>
            <span className="font-mono text-primary">
              -{formatCurrency(summary.totalDiscountApplied)}
            </span>
          </div>
        )}

        <div className="border-t pt-2 flex items-center justify-between font-medium">
          <span>Adjusted Total</span>
          <span className="font-mono">{formatCurrency(summary.adjustedTotal)}</span>
        </div>

        <Badge variant="default" className="w-full justify-center">
          <TrendingDown className="h-3 w-3 mr-1" />
          {summary.savingsPercentage.toFixed(2)}% Total Savings
        </Badge>
      </CardContent>
    </Card>
  );
};
